/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { groupBy, map, merge } from 'lodash';
import { TFunction } from 'i18next';

import WDT_CLASSROOM_PROGRESS_BY_STUDENTS_QUERY from 'graph/reports/wdt-classroom-progress-by-students-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { ModuleType, ReportEntityType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { WdtClassroomProgressByStudentsData } from 'types/schema-generated-types';
import ReportLink from 'components/ReportsModule/components/common/ReportLink';

const getColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'student',
    label: t('Student'),
    sortKey: 'student',
    getValue: item => (
      <ReportLink key={item.id} entityType={ReportEntityType.STUDENT} entityId={item.id}>
        {item.student}
      </ReportLink>
    ),
    sortable: true
  },
  {
    id: 'level1',
    label: t('Completed 1 Star'),
    sortKey: 'level1',
    getValue: item => <Typography>{item.level1}</Typography>,
    sortable: true
  },
  {
    id: 'level2',
    label: t('Completed 2 Star'),
    sortKey: 'level2',
    getValue: item => <Typography>{item.level2}</Typography>,
    sortable: true
  },
  {
    id: 'level3',
    label: t('Completed 3 Star'),
    sortKey: 'level3',
    getValue: item => <Typography>{item.level3}</Typography>,
    sortable: true
  }
];

interface ProgressForStudentsTableViewProps {
  module: ModuleType;
}

const ProgressForStudentsTableView: React.FC<ProgressForStudentsTableViewProps> = ({ module }) => {
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_PROGRESS_BY_STUDENTS_QUERY);
  const { t } = useTranslation('Reports');

  const columns = getColumns(t);

  const reportData: WdtClassroomProgressByStudentsData[] =
    data?.wdtClassroomProgressByStudentsReport?.studentProgress ?? [];

  const mappedReportData = map(groupBy(reportData, 'student.id'), values => ({
    id: values?.[0]?.student?.id,
    student: `${values?.[0]?.student?.firstName} ${values?.[0]?.student?.lastName}`,
    level1: values.find(item => item.level === 1)?.charactersCompletedCount ?? 0,
    level2: values.find(item => item.level === 2)?.charactersCompletedCount ?? 0,
    level3: values.find(item => item.level === 3)?.charactersCompletedCount ?? 0
  }));

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module: module
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  return (
    <ReportCard
      title={t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}
      loading={loading}
      reportData={reportData}
    >
      <LwtTable columns={columns} data={mappedReportData} />
    </ReportCard>
  );
};

export default ProgressForStudentsTableView;
